/**
* Copyright by:

* by ESS-Ehrhardt Software Solutions & Service GmbH
* Basselscheider Str. 55
* D-56281 Emmelshausen
* +49 (0) 67 42 / 87 27 0
* info@epg.com
*/
import { Component, Input, OnChanges, OnDestroy, OnInit, SimpleChanges } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Logger, LogService } from '@epg/angular-base-lib';
import {
  faChevronDown,
  faChevronUp,
  faExclamationTriangle,
  faFilter,
  faSearch
} from '@fortawesome/pro-regular-svg-icons';
import { Subscription } from 'rxjs';
import { TaskAssignmentType } from '../../../../../../enums/task-assignment-type.enum';
import { TaskStatus } from '../../../../../../enums/task-status.enum';
import { TaskReferenceModel } from '../../../../../../models/task-reference.model';
import { TaskModel } from '../../../../../../models/task.model';
import { CustomDateFormatService } from '../../../../../../services/custom-date-format/custom-date-format.service';
import { EmployeeService } from '../../../../../../services/employee.service';
import { WorkflowService } from '../../../../../../services/workflow.service';
import { SYSTEM_DISPLAY_NAME } from '../../../../../shared/constants';

@Component({
  selector: 'app-task-general-data',
  templateUrl: './task-general-data.component.html',
  styleUrl: './task-general-data.component.scss',
  standalone: false
})
export class TaskGeneralDataComponent implements OnInit, OnChanges, OnDestroy {
  @Input() task: TaskModel | undefined;
  @Input() enableLink: boolean = true;

  TaskStatus = TaskStatus;
  TaskAssignmentType = TaskAssignmentType;
  workflowName: string = '';
  showAllTags = false;
  showAllReferences = false;
  showAllButtonText = $localize`:@@showAllButton:Show all`;
  showLessButtonText = $localize`:@@showLessButton:Show less`;

  faExclamationTriangle = faExclamationTriangle;
  faFilter = faFilter;
  faSearch = faSearch;
  faChevronUp = faChevronUp;
  faChevronDown = faChevronDown;
  displayNameMap: Record<string, string> = {};
  visibleParticipantIds: string[] = [];
  employeesChangedSubscription: Subscription = new Subscription();

  private log: Logger;

  constructor(
    private employeeService: EmployeeService,
    private workflowService: WorkflowService,
    private route: ActivatedRoute,
    private router: Router,
    public customDateFormatService: CustomDateFormatService,
    private logService: LogService
  ) {
    this.log = logService.getLogger(this);
  }

  async ngOnInit() {
    this.workflowName = await this.getWorkflowNameFromId(this.task?.workflowId || '');
    this.employeesChangedSubscription = this.employeeService.employeesChanged.subscribe(async () => {
      await this.resolveDisplayNames();
    });
    await this.resolveDisplayNames();
  }

  async ngOnChanges(changes: SimpleChanges): Promise<void> {
    if (changes['task']) {
      await this.resolveDisplayNames();
    }
  }

  ngOnDestroy(): void {
    this.employeesChangedSubscription.unsubscribe();
  }

  employeeExists(id: string): boolean {
    return (
      this.employeeService.getEmployeeUserNameFromID(id) !== undefined ||
      (this.displayNameMap[id] !== undefined && this.displayNameMap[id] !== id)
    );
  }

  getEmployeeName(id: string): string {
    return this.displayNameMap[id] ?? this.employeeService.getEmployeeUserNameFromID(id) ?? id;
  }

  getParticipantNames(ids: string[]): string {
    return ids.map((id) => this.getEmployeeName(id)).join(', ');
  }

  getEmployeeGroupName(id: string) {
    var grpName = this.employeeService.getEmployeeGroupNameFromID(id);
    if (grpName) {
      return grpName;
    } else return id;
  }

  async getWorkflowNameFromId(id: string): Promise<string> {
    try {
      const workflowName = await this.workflowService.getWorkflowNameFromId(id);
      return workflowName ?? '';
    } catch (error) {
      this.log.error('Error fetching workflow: ', error);
      return id;
    }
  }

  getWorkflowRoute(id: string): string {
    const combinedId = this.workflowService.getCombinedIdFromWorkflowId(id);
    const targetId = combinedId ?? id;

    return `home/workflow/${targetId}`;
  }

  getStartedAt(): string | undefined {
    const startedEntry = this.task?.time_tracking?.find((entry) => entry.name === 'start');
    if (!startedEntry?.start) {
      return undefined;
    }

    return this.customDateFormatService.formatDate(startedEntry.start.toString());
  }

  getStartedBy(): string | undefined {
    const startedEntry = this.task?.time_tracking?.find((entry) => entry.name === 'start');
    if (!startedEntry?.employee_id) {
      return undefined;
    }

    return this.getEmployeeName(startedEntry.employee_id);
  }

  getClosedAt(): string | undefined {
    const startedEntry = this.task?.time_tracking?.find((entry) => entry.name === 'start');
    if (!startedEntry?.start) {
      return undefined;
    }

    return this.customDateFormatService.formatDate(startedEntry.start.toString());
  }

  getClosedBy(): string | undefined {
    const startedEntry = this.task?.time_tracking?.find((entry) => entry.name === 'start');
    if (!startedEntry?.employee_id) {
      return undefined;
    }

    return this.getEmployeeName(startedEntry.employee_id);
  }

  getCurrentItemTitle(): string | undefined {
    if (!this.task?.record || this.task.record.length === 0) {
      return undefined;
    }

    const currentRecord = this.task.record[this.task.currentState];
    if (currentRecord?.itemTitle) {
      return currentRecord.itemTitle;
    }

    return this.task.record[this.task.record.length - 1]?.itemTitle;
  }

  setReferenceFilter(reference: TaskReferenceModel) {
    let params = { ...this.route.snapshot.queryParams };
    params.refType = reference.refType;
    params.refValue = reference.refValue;

    this.router.navigate([], {
      queryParams: params
    });
  }

  private async resolveDisplayNames(): Promise<void> {
    const task = this.task;
    this.visibleParticipantIds = [];

    const ids = new Set<string>();

    if (task?.assignedToEmployeeOrEmployeegroup === TaskAssignmentType.EMPLOYEE) {
      task.assignedTo?.forEach((id) => ids.add(id));
    }

    task?.executedBy?.forEach((id) => ids.add(id));

    if (task?.claimedBy) {
      ids.add(task.claimedBy);
    }

    if (task?.closedBy) {
      ids.add(task.closedBy);
    }

    task?.time_tracking?.forEach((entry) => {
      if (entry.employee_id) {
        ids.add(entry.employee_id);
      }
    });

    await Promise.all(
      Array.from(ids).map(async (id) => {
        this.displayNameMap[id] = await this.employeeService.getDisplayNameForUserOrServiceAccount(id);
      })
    );

    if (this.task !== task) {
      return;
    }

    this.visibleParticipantIds = task?.executedBy?.filter((id) => this.getEmployeeName(id) !== SYSTEM_DISPLAY_NAME) ?? [];
  }
}
